import { Button, Flex, IconFont, Text } from '@byecode/ui'
import type { AliPayOrderInfoProtocol } from '@lighthouse/core'
import { PayStatus } from '@lighthouse/core'
import { getBrandInfo } from '@lighthouse/shared'
import { Anchor } from '@mantine/core'
import { multiply, subtract } from 'rambda'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAsyncRetry } from 'react-use'
import styled from 'styled-components'

import * as srv from '@/services'

const SCxContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
`

const SCxLogo = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`

const SCxOrderInfo = styled.div`
    width: 100%;
    padding: 12px 16px;
    border-radius: 8px;
    background: var(--color-gray-100);
    line-height: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`

const SCxIframe = styled.iframe`
    width: 106px;
    height: 106px;
    overflow: visible;
`

interface WeChatPayProps {
    getPayInfo?: () => Promise<AliPayOrderInfoProtocol>
    onPaySuccess?: () => void
    onPayFailed?: (status: PayStatus) => void
}

const loadingHtmlString =
    '<style>html,body{width:105px;height:105px; margin: 0; padding: 0;} .loading {width: 60px;height: 100%; margin: 0 auto;display: flex;align-items: center;justify-content: space-between;}.loading > div {width: 6px;height: 6px;background-color: "#667085";border-radius: 50%;animation: scale 1s ease-in-out infinite;}.loading div:nth-of-type(1) {animation-delay: -1s;}.loading div:nth-of-type(2) {animation-delay: -0.9s;}.loading div:nth-of-type(3) {animation-delay: -0.8s;}.loading div:nth-of-type(4) {animation-delay: -0.7s;}.loading div:nth-of-type(5) {animation-delay: -0.6s;}@keyframes scale {0% {transform: scale(1);}50% {transform: scale(1.8);}100% {transform: scale(1);}}</style><div class="loading"><div style="background: #d0d5dd" ></div><div style="background: #d0d5dd" ></div><div style="background: #d0d5dd" ></div><div style="background: #d0d5dd" ></div><div style="background: #d0d5dd" ></div></div>'

export const Pay: React.FunctionComponent<WeChatPayProps> = ({ getPayInfo, onPaySuccess, onPayFailed }) => {
    const [isCheck, setIsCheck] = useState(false)

    // 检查过期时间
    const timeRef = useRef(Date.now())
    const [isExpired, setIsExpired] = useState(false)

    const { value, loading, retry } = useAsyncRetry(async () => {
        setIsCheck(false)
        if (!getPayInfo) {
            return
        }
        const res = await getPayInfo()
        setIsCheck(true)
        timeRef.current = Date.now()
        return res
    }, [getPayInfo])

    const isPromo = useMemo(() => value?.discount && value?.discount < 1, [value?.discount])

    // 检查非jsapi微信支付，是否支付成功
    const checkPayStatus = useCallback(async () => {
        if (!value?.outTradeNo) {
            return null
        }

        const code = await srv.getPayStatus(value.outTradeNo)
        if (code === PayStatus.NOTPAY) {
            return
        }

        if (code === PayStatus.SUCCESS) {
            onPaySuccess?.()
            return
        }

        onPayFailed?.(code)
    }, [onPayFailed, onPaySuccess, value?.outTradeNo])

    // 轮询检查非jsapi微信支付
    useEffect(() => {
        if (!isCheck) {
            return
        }

        const timer = setInterval(() => {
            checkPayStatus()
            if (Date.now() - timeRef.current >= 5 * 60 * 1000) {
                setIsExpired(true)
                clearInterval(timer)
            }
        }, 3000)

        return () => {
            clearInterval(timer)
        }
    }, [checkPayStatus, isCheck])

    return (
        <SCxContainer>
            <SCxLogo>
                <IconFont type="AliPay" size={60} />
                {/* <Text color="#226BF3" weight={600}>
                    支付宝支付
                </Text> */}
            </SCxLogo>
            <Text>请使用支付宝扫一扫进行支付</Text>

            {loading ? (
                <div style={{ height: 105 }} />
            ) : (
                <div style={{ position: 'relative' }}>
                    {isExpired && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'rgba(0,0,0,0.5)'
                            }}
                        >
                            <Button
                                type="text"
                                style={{ color: '#fff' }}
                                onClick={() => {
                                    setIsExpired(false)
                                    timeRef.current = Date.now()
                                    retry()
                                }}
                            >
                                已过期，点击刷新
                            </Button>
                        </div>
                    )}
                    <SCxIframe
                        title="pay"
                        srcDoc={` <html>
                             ${loadingHtmlString}
                             ${value?.nativeResponse.codeUrl || ''}
                        </body>
                        </html>`}
                    />
                    {/* <QRCode size={156} level="H" value={value?.nativeResponse.codeUrl ?? ''} renderAs="svg" /> */}
                </div>
            )}
            <Flex direction="column" alignItems="center">
                <Text>使用支付宝扫码支付</Text>
                <Text>
                    支付即视为你同意
                    <Anchor
                        // size="sm"
                        href={getBrandInfo().account.privacyAgreement}
                        color="var(--color-blue-500)"
                        weight={600}
                        target="_blank"
                    >
                        《ByeCode会员服务协议》
                    </Anchor>
                </Text>
            </Flex>

            <SCxOrderInfo>
                <Flex justifyContent="space-between">
                    <Text size={16} color="var(--color-gray-500)">
                        商品信息
                    </Text>
                    <Text>{value?.prodDesc}</Text>
                </Flex>
                <Flex justifyContent="space-between">
                    <Text size={16} color="var(--color-gray-500)">
                        实付金额
                    </Text>
                    <Text>￥{value?.realPrice}</Text>
                </Flex>
                {isPromo && value?.discount && (
                    <>
                        <Flex justifyContent="space-between">
                            <Text size={16} color="var(--color-gray-500)">
                                原价
                            </Text>
                            <Text color="var(--color-gray-500)">￥{value?.originalPrice}</Text>
                        </Flex>
                        <Flex justifyContent="space-between">
                            <Text size={16} color="var(--color-gray-400)">
                                优惠
                            </Text>
                            <Text color="var(--color-main)">
                                {multiply(value.discount, 100).toFixed(0)}折 -￥
                                {subtract(value?.originalPrice ?? 0, value?.realPrice ?? 0).toFixed(2)}
                            </Text>
                        </Flex>
                    </>
                )}
            </SCxOrderInfo>
        </SCxContainer>
    )
}
